/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/navigation/NotificationDropdown.tsx
 * PURPOSE: Real-time notification dropdown với Vietnamese messaging
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: UI Components, Vietnamese F&B User Experience
 * - Impact: User Experience, Interface Quality, Accessibility
 * - Stakeholders: End Users, UI/UX Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, UI Components
 * - Pattern: React Component, Design System, Reusable Components
 * - Dependencies: React 18, TypeScript, TailwindCSS, shadcn/ui
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 *  */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  XMarkIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  BellIcon
} from '@heroicons/react/24/outline';

import { cn } from '@/utils/cn';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification } from '@/hooks/useNotifications';

interface NotificationDropdownProps {
  onClose: () => void;
}

// This component now uses the enhanced useNotifications hook

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { 
    notifications, 
    markAsRead, 
    removeNotification, 
    markAllAsRead,
    isLoading,
    isConnected,
    unreadCount
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Get notification icon based on type
  const getNotificationIcon = (type: Notification['type']) => {
    const iconClasses = "w-5 h-5";
    switch (type) {
      case 'warning':
        return <ExclamationTriangleIcon className={cn(iconClasses, "text-amber-500")} />;
      case 'success':
        return <CheckCircleIcon className={cn(iconClasses, "text-green-500")} />;
      case 'task':
        return <ClockIcon className={cn(iconClasses, "text-coffee-600")} />;
      case 'schedule':
        return <ClockIcon className={cn(iconClasses, "text-purple-500")} />;
      default:
        return <InformationCircleIcon className={cn(iconClasses, "text-gray-500")} />;
    }
  };

  // Format timestamp to Vietnamese relative time
  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - notificationTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Vừa xong';
    if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} giờ trước`;
    return `${Math.floor(diffInMinutes / 1440)} ngày trước`;
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  // unreadCount is now provided by the hook

  return (
    <div
      ref={dropdownRef}
      className="fixed top-16 right-4 w-96 bg-white border border-gray-200 rounded-lg shadow-2xl py-2 max-h-[80vh] overflow-hidden"
      style={{ zIndex: 9999 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Thông báo
            {unreadCount > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {unreadCount}
              </span>
            )}
          </h3>
          {/* Connection status */}
          <div className={cn(
            "w-2 h-2 rounded-full",
            isConnected ? "bg-green-500" : "bg-yellow-500"
          )} title={isConnected ? "Đã kết nối" : "Đang kết nối lại..."} />
        </div>
        
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-coffee-600 hover:text-coffee-800 font-medium"
            >
              Đánh dấu đã đọc
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Đóng thông báo"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-coffee-600"></div>
            <span className="ml-3 text-gray-500">Đang tải...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-4 text-center">
            <BellIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-400">Không có thông báo nào</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-l-4",
                  !notification.isRead 
                    ? "bg-coffee-50 border-l-coffee-500"
                    : "border-l-transparent",
                  notification.priority === 'high' && "bg-red-50 border-l-red-500",
                  notification.actionUrl && "cursor-pointer"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <h4 className={cn(
                        "text-sm font-medium truncate",
                        !notification.isRead ? "text-gray-900" : "text-gray-700"
                      )}>
                        {notification.title}
                      </h4>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="ml-2 p-1 hover:bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Xóa thông báo"
                      >
                        <XMarkIcon className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                    
                    <p className={cn(
                      "text-xs mt-1 line-clamp-2",
                      !notification.isRead ? "text-gray-700" : "text-gray-500"
                    )}>
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(notification.timestamp)}
                      </span>
                      
                      {notification.priority === 'high' && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                          Quan trọng
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer — GN-1: the "Xem tất cả thông báo" button used to navigate('/notifications'),
          but no such route and no notifications-list page component exist anywhere in the app,
          so it was a dead link that dumped the user on the 404/catch-all route. Removed until
          the list page is actually built (tracked as follow-up). The dropdown itself is fully
          functional: it lists, marks-read and deletes via REST. */}
    </div>
  );
};